import React, { useEffect, useState } from "react";
import { transferTokensService } from "../../../../../../services/transferTokensService";
import { isAddress } from "viem";
import { CurrensySelector } from "./components/CurrensySelector";
import { createTokensList } from "./utils";
import { QRScanner } from "../../../../../QrReader";
import { toast } from "react-toastify";
import { QRScannerIcon } from "../../../../../svgComponents/QRScannerIcon";
import { useTokensStore } from "../../../../../../store/tokensStore";
import { useContextProvider } from "../../../../../../context";
import { getEthersSigner } from "../../../../../../utils/wagmi.utils";
import { polygon } from "viem/chains";
import Button from "../../../../../ButtonCustom";
import { Loader } from "../../../../../Loader";

export const ModalContentSendAssets = () => {
  const [amountValue, setAmountValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  // const { currentNetwork } = useNetworksStore();
  const {
    walletAddress,
    nativeCurrency,
    refetchNativeCurrencyBalance,
    jsonRpcProvider,
    getUserTokensBalance,
  } = useContextProvider();
  const { userTokens } = useTokensStore();
  const [tokens, setTokens] = useState([]);

  useEffect(() => {
    const tokens = createTokensList(userTokens, nativeCurrency);

    setTokens(tokens);
  }, [userTokens, nativeCurrency, isProcessing]);

  useEffect(() => {
    if (!selectedToken) setSelectedToken(tokens[0]);

    if (selectedToken) {
      const token = tokens.find(
        (token) => token.symbol === selectedToken.symbol
      );
      if (token) setSelectedToken(token);
    }
  }, [tokens]);

  const handleChangeToken = (token) => {
    setSelectedToken(token);
  };

  const onChangeAmountHandler = (e) => {
    const regex = /^\d*\.?\d{0,6}$/;
    const value = e.target.value;

    if (regex.test(value)) {
      setAmountValue(value);
    }
  };

  const setMaxNativeAmount = async () => {
    if (
      !nativeCurrency ||
      !recipientAddress ||
      !isAddress(recipientAddress) ||
      !jsonRpcProvider ||
      !nativeCurrency.formatted ||
      !walletAddress
    )
      return;

    try {
      const max = await transferTokensService.getMaxTokensAmount({
        provider: jsonRpcProvider,
        to: recipientAddress,
        value: nativeCurrency.formatted,
        walletAddress,
        decimals: nativeCurrency.decimals,
      });

      setAmountValue(String(max));
    } catch (error) {
      console.error("Error while getting max tokens amount", error);

      toast.error(error?.message || "Failed");
    }
  };

  const handleSendNativeToken = async () => {
    const signer = await getEthersSigner();
    if (
      !recipientAddress ||
      !signer ||
      !isAddress(recipientAddress) ||
      !nativeCurrency ||
      !jsonRpcProvider
    )
      return;

    try {
      setIsProcessing(true);

      await transferTokensService.sendNativeTokens({
        signer,
        to: recipientAddress,
        value: amountValue,
        decimals: nativeCurrency.decimals,
        provider: jsonRpcProvider,
      });

      refetchNativeCurrencyBalance();

      toast.success("Success!");

      setAmountValue("");

      setIsProcessing(false);
    } catch (error) {
      console.error("Error while sending tokens", error);

      !error.message.includes("user rejected") &&
        toast.error(error?.message || "Failed");

      setIsProcessing(false);
    }
  };

  const handleSendDepositToken = async () => {
    const signer = await getEthersSigner();
    if (
      !recipientAddress ||
      !signer ||
      !isAddress(recipientAddress) ||
      !nativeCurrency ||
      !selectedToken ||
      !jsonRpcProvider
    )
      return;

    try {
      setIsProcessing(true);

      await transferTokensService.sendToken({
        tokenAddress: selectedToken?.address,
        to: recipientAddress,
        amount: amountValue,
        signer,
        decimals: userTokens[0].decimals,
        provider: jsonRpcProvider,
      });

      await getUserTokensBalance();

      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);

      !error.message.includes("user rejected") &&
        toast.error(error?.message || "Failed");

      console.error(error);
    }
  };

  const handleSendTokens = async (token) => {
    if (!token) return;

    setIsProcessing(true);

    if (token.isNanite) await handleSendNativeToken();
    if (!token.isNanite) await handleSendDepositToken();
  };

  const handleSetMaxTransferableValue = async (token) => {
    setIsProcessing(true);

    if (token.isNanite) await setMaxNativeAmount();

    if (!token.isNanite) setAmountValue(token.value);

    setIsProcessing(false);
  };

  return (
    <div className='px-[32px] pt-[12px] pb-[24px] font-Inter relative'>
      <p className='font-medium leading-[24px] text-base mb-3 dark:text-white'>
        {`Send your accets to another wallet on ${polygon.name}.`}
      </p>

      <div className='relative placeholder:text-black-25 dark:placeholder:text-white-25 rounded-8 w-full text-black px-4 pr-12 py-3 text-base bg-blue-light-10 dark:bg-blue-darkMode-30 dark:!text-white'>
        <input
          className='outline-none transition-all bg-transparent w-full'
          type='text'
          value={recipientAddress || ""}
          onChange={(e) => setRecipientAddress(e.target.value)}
          placeholder='Recipient wallet address'
          disabled={!tokens.length || isProcessing}
        />
        <button
          onClick={() => setIsCameraOpen(true)}
          className='absolute right-4 top-1/2 transform -translate-y-1/2 text-black-50 hover:text-black transition-colors dark:text-white-50 dark:hover:text-white'
        >
          <QRScannerIcon className='w-6 h-6' />
        </button>
      </div>

      <div>
        <div className='my-3 flex justify-between items-center text-black dark:text-white text-sm'>
          <p>You send</p>
          {selectedToken && (
            <div className='flex justify-between items-center gap-2'>
              <p>
                Balance:{" "}
                {selectedToken?.value
                  ? Math.floor(parseFloat(selectedToken.value) * 1000) / 1000
                  : "0.000"}{" "}
                {selectedToken.symbol}
              </p>
              <Button
                variant='bordered'
                onClick={() => handleSetMaxTransferableValue(selectedToken)}
                disabled={
                  !recipientAddress ||
                  !walletAddress ||
                  !selectedToken?.value ||
                  !parseFloat(selectedToken.value) ||
                  isProcessing
                }
                className='disabled:cursor-not-allowed !h-[24px] !px-2'
              >
                Max
              </Button>
            </div>
          )}
        </div>

        <div className='mb-4 relative text-base'>
          <input
            className='outline-none px-4 py-3.5 text-black dark:placeholder:text-white-25 pr-[145px] bg-white-4 transition-all placeholder:text-black-25 bg-blue-light-10 dark:bg-blue-darkMode-30 dark:!text-white rounded-8 w-full'
            type='number'
            value={amountValue}
            onChange={onChangeAmountHandler}
            placeholder='00.00'
            onKeyDown={(evt) =>
              ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()
            }
            disabled={!tokens.length || isProcessing}
          />
          <div className='flex items-center justify-center gap-2 absolute top-2/4 right-4 translate-y-[-50%]'>
            {!!tokens.length && (
              <CurrensySelector
                tokens={tokens}
                selectedToken={selectedToken}
                onChangeToken={handleChangeToken}
                disabled={!tokens.length || isProcessing}
              />
            )}
          </div>
        </div>

        <Button
          variant='filled'
          onClick={() => handleSendTokens(selectedToken)}
          disabled={
            !selectedToken?.value ||
            !parseFloat(selectedToken.value) ||
            !amountValue ||
            !recipientAddress ||
            !isAddress(recipientAddress) ||
            isProcessing ||
            parseFloat(amountValue) > parseFloat(selectedToken.value)
          }
          className='w-full justify-center !h-[56px] !text-base !font-[500]'
        >
          {isProcessing ? <Loader ratio={24} /> : "Send"}
        </Button>
      </div>

      {isCameraOpen && (
        <QRScanner
          setInputValue={setRecipientAddress}
          setIsCameraOpen={setIsCameraOpen}
          isCameraOpen={isCameraOpen}
          searchValue={recipientAddress || ""}
          setScannerError={() => toast("QR Scanner error!")}
        />
      )}
    </div>
  );
};
